import React, { useEffect, useState, useCallback } from "react";
import MUIDataTable from "mui-datatables";
import {
  Button,
  IconButton,
  Tooltip,
  DialogContent,
  Dialog,
  DialogActions,
} from "@mui/material";
import { toast } from "react-toastify";
import moment from "moment";
import {
  Close,
  RemoveRedEyeOutlined,
  Download as DownloadIcon,
} from "@mui/icons-material";
import Axios from "../axios";
import { CSVLink } from "react-csv";
import "react-quill/dist/quill.snow.css";
import { Helmet } from "react-helmet";

const DailyTransactions = () => {
  const [open, setOpen] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [page, setPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [search, setSearch] = useState("");
  const [downloadData, setDownloadData] = useState([]);
  const [transactionDetails, setTransactionDetails] = useState({
    bookingId: "",
    creationDate: "",
    tripStartDate: "",
    tripEndDate: "",
    customerEmail: "",
    guestNames: "",
    rooms: 0,
    nights: "",
    roomNights: "",
    description: "",
    facilities: [],
    baseRate: "",
    totalRate: "",
    bookingStatus: "",
    supplierPropertyID: "",
    propertyID: "",
    propertyName: "",
    propertyAddress: "",
    propertyRegion: "",
    propertyStarRating: "",
    occupancy: "",
    cancelationPolicy: "",
    cancelationDate: "",
  });

  const headers = [
    { label: "Booking ID", key: "bookingID" },
    { label: "Booking Date", key: "bookingDate" },
    { label: "Check in date", key: "tripStartDate" },
    { label: "Checkout date", key: "tripEndDate" },
    { label: "Customer email", key: "email" },
    { label: "Guest Name", key: "guestNames" },
    { label: "Room Count", key: "roomsCount" },
    { label: "Nights", key: "nights" },
    { label: "Room Nights", key: "nights" },
    { label: "Room type booked", key: "rooms" },
    { label: "Value adds included", key: "valueAddedService" },
    { label: "Sell Rate(USD)", key: "sellRate" },
    { label: "Net Rate(USD)", key: "netRate" },
    { label: "Hotel Confirmation", key: "hotelConfirmation" },
    { label: "Supplier Property ID", key: "supplierPropertyID" },
    { label: "Property ID", key: "propertyID" },
    { label: "Property Name", key: "propertyName" },
    { label: "Property Address", key: "propertyAddress" },
    { label: "Property Region", key: "propertyRegion" },
    { label: "Property star rating", key: "propertyStarRating" },
    { label: "Adults", key: "adults" },
    { label: "Children", key: "children" },
    { label: "Cancellation Policy", key: "cancellationPolicy" },
    { label: "Cancelation Number", key: "bookingID" },
    { label: "Cancelation Date", key: "cancelationDate" },
    { label: "Refund Amount(USD)", key: "RefundAmountIfAny" },
  ];

  const fetchTransactions = useCallback((pageNum = 0, searchTerm = "") => {
    Axios.post(process.env.REACT_APP_BACKEND + "get-all-arrival", {
      page: pageNum,
      searchChar: searchTerm,
    })
      .then((resp) => {
        const data = resp?.data?.data;
        if (data) {
          setTransactions(resp?.data?.data?.data || []);
          setTotalCount(resp?.data?.data?.totalCount || 0);
        }
      })
      .catch((err) => {
        const errorMessage =
          err?.response?.data?.message || "Failed to fetch transactions";
        toast.error(errorMessage);
      });
  }, []);

  const fetchDownloadData = useCallback(() => {
    Axios.post(process.env.REACT_APP_BACKEND + "get-all-csv-data")
      .then((resp) => {
        setDownloadData(resp?.data?.data || []);
      })
      .catch((err) => {
        const errorMessage =
          err?.response?.data?.message || "Failed to fetch download data";
        toast.error(errorMessage);
      });
  }, []);

  useEffect(() => {
    fetchTransactions(0, "");
    fetchDownloadData();
  }, [fetchTransactions, fetchDownloadData]);

  const calculateNights = (startDate, endDate) => {
    const end = moment(endDate);
    const start = moment(startDate);
    const calculatedNights = end.diff(start, "days");
    return calculatedNights === 1 ? "1 day" : `${calculatedNights} days`;
  };

  const handleOpenDetails = (transaction) => {
    if (!transaction) return;

    const adultsCount =
      transaction?.rates?.[0]?.occupancies?.[0]?.numOfAdults || 0;
    const childCount =
      transaction?.rates?.[0]?.occupancies?.[0]?.numOfChildren || 0;

    setTransactionDetails({
      bookingId: transaction?.bookingId || "",
      creationDate: transaction?.creationDate || "",
      tripStartDate: transaction?.tripStartDate || "",
      tripEndDate: transaction?.tripEndDate || "",
      customerEmail: transaction?.billingContact?.contact?.email || "",
      guestNames: transaction?.guestNames || "",
      rooms: transaction?.rooms?.length || 0,
      nights: calculateNights(
        transaction?.tripStartDate,
        transaction?.tripEndDate
      ),
      roomNights: transaction?.roomNights || "",
      description: transaction?.rooms?.[0]?.description || "",
      facilities: transaction?.hotel?.facilities || [],
      baseRate: transaction?.rates?.[0]?.baseRate || "",
      totalRate: transaction?.rates?.[0]?.totalRate || "",
      bookingStatus: transaction?.bookingStatus || "",
      supplierPropertyID: transaction?.rates?.[0]?.providerHotelId || "",
      propertyID: transaction?.hotel?.id || "",
      propertyName: transaction?.hotel?.name || "",
      propertyAddress: transaction?.hotel?.contact?.address?.line1 || "",
      propertyRegion: transaction?.hotel?.contact?.address?.country?.name || "",
      propertyStarRating: transaction?.hotel?.starRating || "",
      occupancy: `${adultsCount} adults, ${childCount} children`,
      cancelationPolicy: transaction?.rates?.[0]?.refundability || "",
      cancelationDate: transaction?.cancellationDate || "",
    });

    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const options = {
    responsive: "standard",
    selectableRows: "none",
    filter: false,
    viewColumns: false,
    rowsPerPageOptions: [],
    serverSide: true,
    count: totalCount,
    page,
    download: false,
    onTableChange: (action, tableState) => {
      if (action === "changePage") {
        fetchTransactions(tableState.page, search);
        setPage(tableState.page);
      }
    },
    onSearchChange: (searchText) => {
      fetchTransactions(page, searchText);
      setSearch(searchText);
    },
    customToolbar: () => (
      <Tooltip title="Download">
        <IconButton>
          <CSVLink
            data={downloadData}
            headers={headers}
            filename={"DailyTransactionList.csv"}
          >
            <DownloadIcon className="downLoadCSVIcon" />
          </CSVLink>
        </IconButton>
      </Tooltip>
    ),
  };

  const columns = [
    {
      name: "bookingID",
      label: "Booking ID",
      options: {
        filter: true,
        sort: false,
        customBodyRenderLite: (dataIndex) => {
          return <p>{transactions[dataIndex]?.bookingId || ""}</p>;
        },
      },
    },
    {
      name: "supplierBookingID",
      label: "Supplier Booking ID",
      options: {
        filter: true,
        sort: false,
        customBodyRenderLite: (dataIndex) => {
          return <p>{transactions[dataIndex]?.bookingId || ""}</p>;
        },
      },
    },
    {
      name: "coRelationID",
      label: "Co-Relation ID",
      options: {
        filter: true,
        sort: false,
        customBodyRenderLite: (dataIndex) => {
          return <p>{transactions[dataIndex]?.bookingId || ""}</p>;
        },
      },
    },
    {
      name: "zhID",
      label: "ZH ID",
      options: {
        filter: true,
        sort: false,
        customBodyRenderLite: (dataIndex) => {
          return <p>{transactions[dataIndex]?.bookingId || ""}</p>;
        },
      },
    },
    {
      name: "creationDate",
      label: "Booking Date",
      options: {
        filter: true,
        sort: false,
        customBodyRenderLite: (dataIndex) => {
          return (
            <div>
              {moment(transactions[dataIndex]?.creationDate).format(
                "YYYY-MM-DD HH:mm:ss"
              )}
            </div>
          );
        },
      },
    },
    {
      name: "checkInDate",
      label: "Check in date",
      options: {
        filter: true,
        sort: false,
        customBodyRenderLite: (dataIndex) => {
          return (
            <div>
              {moment(transactions[dataIndex]?.tripStartDate).format(
                "YYYY-MM-DD"
              )}
            </div>
          );
        },
      },
    },
    {
      name: "checkOutDate",
      label: "Checkout date",
      options: {
        filter: true,
        sort: false,
        customBodyRenderLite: (dataIndex) => {
          return (
            <div>
              {moment(transactions[dataIndex]?.tripEndDate).format(
                "YYYY-MM-DD"
              )}
            </div>
          );
        },
      },
    },
    {
      name: "customerEmail",
      label: "Customer email",
      options: {
        filter: false,
        sort: false,
        customBodyRenderLite: (dataIndex) => {
          return (
            <p>
              {transactions[dataIndex]?.billingContact?.contact?.email || ""}
            </p>
          );
        },
      },
    },
    {
      name: "roomCount",
      label: "Room Count",
      options: {
        filter: false,
        sort: false,
        customBodyRenderLite: (dataIndex) => {
          return <div>{transactions[dataIndex]?.rooms?.length || 0}</div>;
        },
      },
    },
    {
      name: "options",
      label: "Action",
      options: {
        filter: false,
        sort: false,
        customBodyRenderLite: (dataIndex) => {
          return (
            <Tooltip title="View Daily Transaction">
              <IconButton
                aria-label="View Transaction"
                onClick={() => handleOpenDetails(transactions[dataIndex])}
              >
                <RemoveRedEyeOutlined className="icon-size" />
              </IconButton>
            </Tooltip>
          );
        },
      },
    },
  ];

  const formatFacilities = (facilities) => {
    if (Array.isArray(facilities) && facilities.length > 0) {
      return facilities.map((facility) => facility.name).join(", ");
    }
    return "No facilities available";
  };

  return (
    <>
      <Helmet>
        <title>Sevenly-Daily Transaction</title>
        <meta name="Daily Transaction" content="Daily Transaction" />
      </Helmet>

      <h1 className="mb--12 sevenly-cursor"> — </h1>
      <div className="d-flex justify-content-between mb-2">
        <h2>Daily Transaction Management</h2>
      </div>

      <MUIDataTable
        title={"Daily Transaction List"}
        data={transactions}
        columns={columns}
        options={options}
      />
      <Dialog
        onClose={handleClose}
        aria-labelledby="transaction-dialog-title"
        fullWidth
        open={open}
        maxWidth="xl"
      >
        <div className="d-flex justify-content-between align-center p-3">
          <h3 id="transaction-dialog-title">View Daily Transaction</h3>
          <IconButton onClick={handleClose} aria-label="close">
            <Close />
          </IconButton>
        </div>

        <DialogContent dividers>
          <div className="viewDetials">
            <div className="row">
              <div className="col-md-6">
                <p className="fs-16 fw-600 mb-3">
                  Booking ID:
                  <span className="fw-500 fs-18">
                    {" "}
                    {transactionDetails.bookingId}
                  </span>
                </p>
                <p className="fs-16 fw-600 mb-3">
                  Booking Date:
                  <span className="fw-500 fs-18">
                    {transactionDetails.creationDate
                      ? moment(transactionDetails.creationDate).format(
                          "YYYY-MM-DD"
                        )
                      : ""}
                  </span>
                </p>
                <p className="fs-16 fw-600 mb-3">
                  Check-in Date:
                  <span className="fw-500 fs-18">
                    {transactionDetails.tripStartDate
                      ? moment(transactionDetails.tripStartDate).format(
                          "YYYY-MM-DD"
                        )
                      : ""}
                  </span>
                </p>
                <p className="fs-16 fw-600 mb-3">
                  Check-out Date:
                  <span className="fw-500 fs-18">
                    {transactionDetails.tripEndDate
                      ? moment(transactionDetails.tripEndDate).format(
                          "YYYY-MM-DD"
                        )
                      : ""}
                  </span>
                </p>
                <p className="fs-16 fw-600 mb-3">
                  Customer Email:
                  <span className="fw-500 fs-18">
                    {" "}
                    {transactionDetails.customerEmail}
                  </span>
                </p>
                <p className="fs-16 fw-600 mb-3">
                  Rooms:
                  <span className="fw-500 fs-18">
                    {" "}
                    {transactionDetails.rooms}
                  </span>
                </p>
                <p className="fs-16 fw-600 mb-3">
                  Nights:
                  <span className="fw-500 fs-18">
                    {" "}
                    {transactionDetails.nights}
                  </span>
                </p>
                <p className="fs-16 fw-600 mb-3">
                  Guest Name:
                  <span className="fw-500 fs-18">
                    {" "}
                    {transactionDetails.guestNames}
                  </span>
                </p>
                <p className="fs-16 fw-600 mb-3">
                  Room Type Booked:
                  <span className="fw-500 fs-18">
                    {" "}
                    {transactionDetails.description}
                  </span>
                </p>
                <p className="fs-16 fw-600 mb-3">
                  Values Added:
                  <span className="fw-500 fs-18">
                    {formatFacilities(transactionDetails.facilities)}
                  </span>
                </p>
                <p className="fs-16 fw-600 mb-3">
                  Booking Status:
                  <span className="fw-500 fs-18">
                    {" "}
                    {transactionDetails.bookingStatus}
                  </span>
                </p>
                <p className="fs-16 fw-600 mb-3">
                  Base Rate:
                  <span className="fw-500 fs-18">
                    {" "}
                    {transactionDetails.baseRate}
                  </span>
                </p>
                <p className="fs-16 fw-600 mb-3">
                  Total Rate:
                  <span className="fw-500 fs-18">
                    {" "}
                    {transactionDetails.totalRate}
                  </span>
                </p>
              </div>

              <div className="col-md-6">
                <p className="fs-16 fw-600 mb-3">
                  Supplier Property ID:
                  <span className="fw-500 fs-18">
                    {" "}
                    {transactionDetails.supplierPropertyID}
                  </span>
                </p>
                <p className="fs-16 fw-600 mb-3">
                  Property ID:
                  <span className="fw-500 fs-18">
                    {" "}
                    {transactionDetails.propertyID}
                  </span>
                </p>
                <p className="fs-16 fw-600 mb-3">
                  Property Name:
                  <span className="fw-500 fs-18">
                    {" "}
                    {transactionDetails.propertyName}
                  </span>
                </p>
                <p className="fs-16 fw-600 mb-3">
                  Property Address:
                  <span className="fw-500 fs-18">
                    {" "}
                    {transactionDetails.propertyAddress}
                  </span>
                </p>
                <p className="fs-16 fw-600 mb-3">
                  Property Region:
                  <span className="fw-500 fs-18">
                    {" "}
                    {transactionDetails.propertyRegion}
                  </span>
                </p>
                <p className="fs-16 fw-600 mb-3">
                  Property Star Rating:
                  <span className="fw-500 fs-18">
                    {" "}
                    {transactionDetails.propertyStarRating}
                  </span>
                </p>
                <p className="fs-16 fw-600 mb-3">
                  Occupancy:
                  <span className="fw-500 fs-18">
                    {" "}
                    {transactionDetails.occupancy}
                  </span>
                </p>
                <p className="fs-16 fw-600 mb-3">
                  Cancelation Date:
                  <span className="fw-500 fs-18">
                    {" "}
                    {transactionDetails.cancelationDate}
                  </span>
                </p>
                <p className="fs-16 fw-600 mb-3">
                  Cancelation Policy:
                  <span className="fw-500 fs-18">
                    {" "}
                    {transactionDetails.cancelationPolicy}
                  </span>
                </p>
              </div>
            </div>
          </div>
        </DialogContent>

        <DialogActions>
          <Button className="cancel-button" onClick={handleClose}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default DailyTransactions;
